import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import View from '../common/View';
import Spinner from '../common/Spinner';
import Dashboard from '../views/Dashboard';
import ChooseDestination from '../views/ChooseDestination';
import SignIn from '../views/SignIn';
import WelcomeAuth from '../views/WelcomeAuth';
import Register from '../views/Register';
import ForgotPassword from '../views/ForgotPassword';
import ResetPassword from '../views/ResetPassword';
import Settings from '../views/Settings';
import Copyright from './Copyright';
import './Navigation.css';

const PROTECTED_PATHS = ['/', '/dashboard', '/choose-destination', '/settings'];

const Navigation: React.FC = () => {
  const { authState, isAuthenticated } = useAuth();
  const location = useLocation();

  // Loading state — auth bootstrap in progress. Spinner-only (no copy);
  // the spinner itself is the localization-agnostic affordance.
  if (authState === null) {
    return (
      <View className="loading-container">
        <Spinner />
      </View>
    );
  }

  const onProtected = PROTECTED_PATHS.includes(location.pathname);

  // Public auth routes (always accessible)
  if (!isAuthenticated) {
    return (
      <View className="auth-container">
        <Routes>
          <Route path="/welcome" element={<WelcomeAuth />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Anything else → welcome (preserves return path via query) */}
          <Route
            path="*"
            element={
              <Navigate
                to={`/welcome${onProtected ? `?next=${encodeURIComponent(location.pathname)}` : ''}`}
                replace
              />
            }
          />
        </Routes>
        <Copyright />
      </View>
    );
  }

  // Authenticated (or guest). RN parity: SignIn and Register live inside
  // the AppStack so guests can upgrade their session and any user can
  // switch accounts. Only /welcome (the onboarding entry) redirects to /
  // since authed/guest users are already past that screen.
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/choose-destination" element={<ChooseDestination />} />
      <Route path="/settings" element={<Settings />} />

      <Route path="/welcome" element={<Navigate to="/" replace />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default Navigation;
